import {extractIndoorMapLink} from "../util/util";

const addressIconPaths = {
    location: "M20 10c0 6-8 11-8 11S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    business: "M3 21h18M5 21V3h14v18M9 7h1m4 0h1M9 11h1m4 0h1M10 21v-6h4v6",
    address: "m3 10 9-7 9 7M5 9v12h14V9M9 21v-7h6v7",
    city: "M2 21h20M4 21V10h6v11M10 21V3h7v18M17 21V13h3v8M13 7h1m-1 4h1M6 14h1m-1 3h1",
    state: "m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2V5Zm6-2v16m6-14v16",
    country: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM3 12h18M12 3c-4 4-4 14 0 18 4-4 4-14 0-18Z",
    postal: "M3 5h18v14H3V5Zm0 1 9 7 9-7",
};

const AddressLine = ({line}) => {
    const label = line.split(":", 1)[0].trim().toLowerCase();
    const key = /^(state|province)/.test(label) ? "state"
        : /^(postal|zip)/.test(label) ? "postal" : label;
    const path = addressIconPaths[key];
    return <div className={path ? "flex items-start gap-2 px-1 py-0.5 mb-1" : "mb-1"}>
        {path && <svg className="h-5 w-5 shrink-0 text-slate-500" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={path}/>
        </svg>}
        <span>{line}</span>
    </div>;
};

// Component to render location information safely
const LocationDisplay = ({parsedData}) => {
    const {floor, room, otherLines} = parsedData;

    return (
        <div className="text-slate-700 font-medium">
            {room && (
                <div className="flex items-center gap-2 font-bold px-1 py-0.5 mb-1">
                    <svg className="h-5 w-5 shrink-0 text-blue-900 drop-shadow-md" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 21h16M6 21V3h12v18"/>
                        <path d="M9 21V6l6 2v13M12 13h.01"/>
                    </svg>
                    <span className="text-blue-900 text-[15px]">Room: {room}</span>
                </div>
            )}
            {floor && (
                <div className="flex items-center gap-2 font-bold px-1 py-0.5 mb-1">
                    <svg className="h-5 w-5 shrink-0 text-blue-900 drop-shadow-md" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m12 3 10 5-10 5L2 8l10-5ZM2 12l10 5 10-5M2 16l10 5 10-5"/>
                    </svg>
                    <span className="text-blue-900 text-[15px]">Floor: {floor}</span>
                </div>
            )}
            {otherLines.map((line, index) => (
                <AddressLine key={index} line={line}/>
            ))}
        </div>
    );
};

const CallHistoryRawItem = ({clickAllowed, clickHandler, history}) => {
    const {created, payload} = history;
    const parsedDate = new Date(Date.parse(created));
    const hasResolvedLocations = !!(payload && payload.length && payload[0].rankedLocations.length);
    const {
        gpsReportedLatitude,
        gpsReportedLongitude,
        gpsLatitude,
        gpsLongitude,
        batteryReading
    } = payload && payload.length && payload[0] ? payload[0] : {};
    const drift = gpsReportedLongitude && gpsReportedLatitude
        ? calcCrow(gpsReportedLatitude, gpsReportedLongitude, gpsLatitude, gpsLongitude)
        : null;
    const locations = hasResolvedLocations ? payload[0].rankedLocations.map(location => {
        const {parsedData} = extractIndoorMapLink(location.humanReadableLocation.replace(/(\n)+/g, "\n"));
        const isApLine = line => /^AP(?:\s|:|[-_])/i.test(line);
        const apLines = parsedData.otherLines.filter(isApLine);
        const remainingLines = parsedData.otherLines.filter(line => !isApLine(line));
        const countryIndex = remainingLines.findIndex(line => /^Country\s*:/i.test(line));
        const visibleLines = countryIndex < 0 ? remainingLines : remainingLines.slice(0, countryIndex + 1);
        const moreLines = countryIndex < 0 ? [] : remainingLines.slice(countryIndex + 1);
        return {location, parsedData: {...parsedData, otherLines: visibleLines}, moreLines: [...apLines, ...moreLines]};
    }) : [];

    return <div className={`call-history-item ${clickAllowed ? "cursor-pointer" : ""}`}
                onClick={clickAllowed ? clickHandler : null}>
        <div className="flex flex-row">
            <span className="w-full text-center mb-1 font-medium text-sm text-slate-700">
                {parsedDate.toLocaleTimeString()}
            </span>
        </div>
        <div>
            {locations.map(({location, parsedData}) =>
                <div key={`location-${location.confidence}-${location.tagUsed}`} className="p-2 ml-2">
                    <LocationDisplay parsedData={parsedData}/>
                </div>)}
            {!hasResolvedLocations && <span className="font-medium text-sm pl-2">No Resolved Locations</span>}
        </div>
        <details className="ml-2 mt-2" onClick={event => event.stopPropagation()}>
            <summary className="cursor-pointer text-sm font-medium text-slate-600">More...</summary>
            {locations.map(({location, moreLines}) => <div
                key={`details-${location.confidence}-${location.tagUsed}`} className="p-2">
                {moreLines.map((line, index) => <div key={index} className="mb-1 text-slate-700 font-medium">{line}</div>)}
                <div className="flex flex-row mb-1 mt-4">
                    {ConfidenceIcon}
                    <span className="font-medium text-sm mr-3">{location.confidence}</span>
                    {location.connected ? <ConnectedIcon/> : <DisConnectedIcon/>}
                    <span className="font-medium text-sm mr-3">{location.tagUsed}</span>
                </div>
            </div>)}
        {gpsLatitude && gpsLongitude && <>
            <div className="ml-1 text-slate-800 mt-3 mb-2 flex flex-row text-sm flex-wrap">
                {LocationIcon} {gpsLatitude + ", " + gpsLongitude}
            </div>
        </>
        }
        {drift !== 0 && <div className="ml-2 mt-3 mb-2 flex flex-row text-sm font-medium flex-wrap">
            {DriftIcon} {drift ? drift + " m" : "--"}
        </div>}
        {batteryReading && <div className="ml-2 mt-2 mb-4 flex flex-row text-sm font-medium">
            {BatteryIcon} <span>{batteryReading + " %"}</span>
        </div>}
        </details>
    </div>;
};

export default CallHistoryRawItem;

const ConfidenceIcon = <svg className="h-5 w-5 mr-1 text-blue-600" width="24" height="24" viewBox="0 0 24 24"
                            strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                            strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <rect x="3" y="12" width="6" height="8" rx="1"/>
    <rect x="9" y="8" width="6" height="12" rx="1"/>
    <rect x="15" y="4" width="6" height="16" rx="1"/>
    <line x1="4" y1="20" x2="18" y2="20"/>
</svg>;
const DisConnectedIcon = () => <svg className="h-5 w-5 mr-1 text-blue-700" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9"/>
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" y1="20" x2="12.01" y2="20"/>
</svg>;
const ConnectedIcon = () => <svg className="h-6 w-6 pb-1 mr-1 text-blue-700" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
    <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
    <line x1="12" y1="20" x2="12.01" y2="20"/>
</svg>;
const LocationIcon = <svg className="h-4 w-4 mr-2 text-blue-700" width="24" height="24" viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <path d="M21 3L14.5 21a.55 .55 0 0 1 -1 0L10 14L3 10.5a.55 .55 0 0 1 0 -1L21 3"/>
</svg>;
const BatteryIcon = <svg className="pb-1 h-7 w-7 mr-1 text-blue-700" width="24" height="24" viewBox="0 0 24 24"
                         strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                         strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z"/>
    <path
        d="M6 7h11a2 2 0 0 1 2 2v.5a.5 .5 0 0 0 .5 .5a.5 .5 0 0 1 .5 .5v3a.5 .5 0 0 1 -.5 .5a.5 .5 0 0 0 -.5 .5v.5a2 2 0 0 1 -2 2h-11a2 2 0 0 1 -2 -2v-6a2 2 0 0 1 2 -2"/>
    <line x1="7" y1="10" x2="7" y2="14"/>
    <line x1="10" y1="10" x2="10" y2="14"/>
    <line x1="13" y1="10" x2="13" y2="14"/>
</svg>;
const DriftIcon = <svg width="24" height="24"
                       className="h-7 w-7 pb-1 mr-1 text-blue-700"
                       viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"
                       strokeWidth="3" stroke="#000000" fill="none">
    <path
        d="M17.94,54.81a.1.1,0,0,1-.14,0c-1-1.11-11.69-13.23-11.69-21.26,0-9.94,6.5-12.24,11.76-12.24,4.84,0,11.06,2.6,11.06,12.24C28.93,41.84,18.87,53.72,17.94,54.81Z"/>
    <circle cx="17.52" cy="31.38" r="4.75"/>
    <path
        d="M49.58,34.77a.11.11,0,0,1-.15,0c-.87-1-9.19-10.45-9.19-16.74,0-7.84,5.12-9.65,9.27-9.65,3.81,0,8.71,2,8.71,9.65C58.22,24.52,50.4,33.81,49.58,34.77Z"/>
    <circle cx="49.23" cy="17.32" r="3.75"/>
    <path d="M17.87,54.89a28.73,28.73,0,0,0,3.9.89"/>
    <path d="M24.68,56.07c2.79.12,5.85-.28,7.9-2.08,5.8-5.09,2.89-11.25,6.75-14.71a16.72,16.72,0,0,1,4.93-3"
          strokeDasharray="7.8 2.92"/>
    <path d="M45.63,35.8a23,23,0,0,1,3.88-.95"/>
</svg>

function calcCrow(lat1, lon1, lat2, lon2) {
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const lat1Rad = toRad(lat1);
    const lat2Rad = toRad(lat2);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c * 1000);
}

// Converts numeric degrees to radians
function toRad(Value) {
    return Value * Math.PI / 180;
}
